import dateformat from 'dateformat';

import setCookie from '@stencil/flights-utils/src/set-cookie';
import getCookie from '@stencil/flights-utils/src/get-cookie';

import { EMPTY_STRING, ADULT, CHILD, INFANT } from '@stencil/flights-constants/src/text';

import getDateObject from '@stencil/flights-date-utils/src/get-date-object';
import { getFromUrlObject, getFromUrlObjectForGroundUp } from '../../Home/utils/searchDataUtils';
import addDays from '@stencil/date-utils/src/addDays';
import { setDirectlyLandedOnSrp } from '../../SRP/redux/actions';
import { flightClassList } from '../../Home/redux/actions';
import { updateSearchWidgetDataThunk } from '../../Home/redux/thunks';
import { updateSearchStringForGroundUp } from '../../SRP/redux-lite/actions';
import { v4 as uuidv4 } from 'uuid';
import {
  OMNITURE_SORT,
  OMNITURE_SORT_MWEB,
  FARE_TYPES,
  FARE_IDENTIFIER,
  BOOKING_REVIEW_PAGE_URL,
  OMNITURE_ID
} from '@stencil/flights-constants';

export function isObjectEmpty(obj) {
  return Object.entries(obj).length === 0 && obj.constructor === Object;
}

// format tt (time for delayed ticketing)
export function getDTTime(dt) {
  const dtArr = dt.split('/');
  const adt = dtArr[0];
  const mdt = dtArr[1];
  let val = adt;
  if (mdt || adt === '0:0:0') {
    val = mdt;
  }
  const arr = val.split(':');
  return `${arr[0] !== '0' ? arr[0] + ' days' : ''} ${arr[1] !== '0' ? arr[1] + ' hours' : ''} ${
    arr[2] !== '0' ? arr[2] + ' minutes' : ''
  }`;
}

export const getDateFromYYYYMMDDString = dateInYYYYMMDD => {
  const newDate = new Date(
    dateInYYYYMMDD.substring(0, 4),
    dateInYYYYMMDD.substring(4, 6) - 1,
    dateInYYYYMMDD.substring(6, 8)
  );
  return newDate;
};

export const getDateInYYYYMMDDSeparatedBySlash = dateToModify => {
  const dateVal = dateToModify ? dateToModify : new Date();
  return dateformat(dateVal, 'yyyy/mm/dd');
};

export function dateDiff(date1, date2) {
  const firstDate = new Date(date1);
  firstDate.setHours(0, 0, 0, 0);
  const secondDate = new Date(date2);
  secondDate.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((secondDate.getTime() - firstDate.getTime()) / (24 * 60 * 60 * 1000));
  return diffDays;
}

export function isPastNumberOfDays(date: Date, noOfDays: number) {
  const today = new Date();
  const dateToCompareWith =
    noOfDays > 0 ? new Date(today.setDate(today.getDate() + noOfDays)) : today;
  return date.setHours(0, 0, 0, 0) - dateToCompareWith.setHours(0, 0, 0, 0) < 0;
}

export const getDateInYYYYMMDD = (date: Date) => {
  return dateformat(date, 'yyyymmdd');
};

export const setSrpFromRepriceAddons = (params, dispatch) => {
  const urlObj = getFromUrlObject(params);
  if (urlObj) {
    dispatch(setDirectlyLandedOnSrp({ params: { 0: params }, searchString: '' }));
    dispatch(updateSearchWidgetDataThunk(urlObj));
  }
};

export const setSrpMebLiteFromRepriceAddons = (searchString, isIntl, dispatch) => {
  const urlObj = getFromUrlObjectForGroundUp(searchString, isIntl);
  if (urlObj) {
    dispatch(updateSearchStringForGroundUp(searchString));
    dispatch(updateSearchWidgetDataThunk(urlObj));
  }
};

export const getUniqueId = (key = 'gi_flt_identity') => {
  const val = getCookie(key);
  if (!val) {
    const cookieVal = new Date().getTime();
    setCookie(key, cookieVal, '', '/', window.location.host.replace('www.', ''), '');
    return cookieVal;
  }
  return val;
};

export const getPDTDetails = (fareIdentifier, fareOptionsInfo) => {
  if (fareIdentifier && fareOptionsInfo) {
    const fareOptions = Object.values(fareOptionsInfo);
    const fare = [].concat.apply([], fareOptions);
    const fareDetails = fare.find(o => o['identifier'] == fareIdentifier);
    return fareDetails;
  }
};

export const getSrpQueryParams = (flightSearchData, query = false) => {
  const {
    source,
    destination,
    departure,
    arrival,
    passengers,
    flightClass,
    journeyType,
    travelType
  } = flightSearchData;
  const EMPTY_STRING = '';
  const DOMESTIC = 'domestic';
  const DOMESTIC_TYPE = query ? '-100--' : '-D';
  const INTERNATIONAL_TYPE = query ? '-0' : '-I';
  return `${source.iata}-${destination.iata}-${departure.yyyymmdd}-${
    journeyType === 'rt' ? arrival?.yyyymmdd : EMPTY_STRING
  }-${passengers.adult}-${passengers.child}-${passengers.infant}-${flightClass.value}${
    travelType === DOMESTIC ? DOMESTIC_TYPE : INTERNATIONAL_TYPE
  }`;
};

export const getUrlParam = (flightSearchData, isQuery = false) => {
  const { source, destination, departure, arrival } = flightSearchData;
  if (source.iata === destination.iata) {
    return null;
  }
  if (arrival && departure && arrival.dateObj < departure.dateObj) {
    return null;
  }
  return `air-${getSrpQueryParams(flightSearchData, isQuery)}`;
};

export const getSrpQueryParamsForCityCountry = (
  flightSearchData,
  selectedDestination,
  defaultCityCountryData,
  query = false
) => {
  if (!selectedDestination || !selectedDestination.toCity) {
    return null;
  }

  const { departure } = flightSearchData;
  const { tripDuration, journeyType } = defaultCityCountryData;
  const destination = { iata: selectedDestination?.toCity };
  const arrival =
    journeyType === 'rt' ? getDateObject(addDays(new Date(departure.dateObj), tripDuration)) : null;

  return getUrlParam({ ...defaultCityCountryData, destination, departure, arrival }, query);
};

export const getCookieString = cookieObj => {
  let cookieStr = '';
  if (
    typeof cookieObj === 'object' &&
    !Array.isArray(cookieObj) &&
    cookieObj !== null &&
    Object.keys(cookieObj).length
  ) {
    const arr = Object.keys(cookieObj);
    cookieStr += `${arr[0]}=${cookieObj[arr[0]]};`;
    for (let i = 1; i < arr.length; i++) {
      cookieStr += ` ${arr[i]}=${cookieObj[arr[i]]};`;
    }
  }
  return cookieStr;
};

export const getObjectWithTitle = (dataArray, titleName) => {
  return dataArray?.find(data => data['Title'] === titleName);
};

const fallbackCopyTextToClipboard = text => {
  try {
    var textArea = document.createElement('textarea');
    textArea.value = text;

    // Avoid scrolling to bottom
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand('copy');
  } catch (ex) {
    throw ex;
  } finally {
    document.body.removeChild(textArea);
  }
};

export const copyTextToClipboard = async text => {
  try {
    if (!navigator.clipboard) {
      return fallbackCopyTextToClipboard(text);
    }
    return navigator.clipboard.writeText(text);
  } catch (ex) {
    throw ex;
  }
};

export const getPassengersText = passengers => {
  let txt = `${passengers.adult} ${ADULT}${passengers.adult > 1 ? 's' : EMPTY_STRING}`;
  if (passengers.child) {
    txt += ` ${passengers.child} ${CHILD}${passengers.child > 1 ? 'ren' : EMPTY_STRING}`;
  }
  if (passengers.infant) {
    txt += ` ${passengers.infant} ${INFANT}${passengers.infant > 1 ? 's' : EMPTY_STRING}`;
  }
  return txt;
};

const generateUniqueId = () => {
  return 'xxxxx-xxxxx-xxxx-xxxxx'.replace(/[x]/g, c => {
    const r = Math.floor(Math.random() * 16);
    return r.toString(16);
  });
};

export const getDeviceId = (key = 'gi_flt_session', expiryTime = 525600) => {
  //set default expiry time to an year
  let uid = getCookie(key);
  if (!uid && key !== 'gi_flt_session') {
    const now = new Date();
    const minutes = expiryTime;
    now.setTime(now.getTime() + minutes * 60 * 1000);
    const expiry = now.toUTCString();
    uid = uuidv4();
    setCookie(key, uid, expiry, '/', window.location.host.replace('www.', ''), '');
  }
  return uid;
};

export const generateUniqueUUID = () => {
  return uuidv4();
};

export const getExperimentNameAndVarientId = (expList, expIDAndNameMapping) => {
  const expNameVarientIdArr = [];
  if (Array.isArray(expList) && expList.length) {
    for (let i = 0; i < expList.length; i++) {
      const expId = parseInt(expList[i].exp_experiment_id);
      if (expIDAndNameMapping.hasOwnProperty(expId)) {
        expNameVarientIdArr.push({
          id: expList[i].exp_experiment_variant_id,
          name: expIDAndNameMapping[expId]
        });
      }
    }
  }
  return expNameVarientIdArr;
};

// export const deleteSearchParamsFromGivenSearchQuery = (searchQuery, paramToBeDeletedList) => {
//   if (!searchQuery || !searchQuery.length) {
//     return '';
//   }
//   let searchString = '';
//   const searchQueryArray = searchQuery?.replace('?', '')?.split('&');
//   for (let i = 0; i < searchQueryArray.length; i++) {
//     let deleteParam = false;
//     for (let j = 0; j < paramToBeDeletedList.length; j++) {
//       if (searchQueryArray[i]?.split('=')[0] === paramToBeDeletedList[j]) {
//         deleteParam = true;
//         break;
//       }
//     }
//     if (!deleteParam) {
//       searchString = `${i > 0 ? '&' : '?'}${searchString}`;
//     }
//   }
//   return searchString;
// };

// Omniture logging

export const ddmmyyyDateConvert = (date = '') => {
  if (!date) return '';
  return `${date.slice(0, 2)}:${date.slice(2, 4)}:${date.slice(4)}`;
};

export const sortOmnitureText = (sortSettings, isMweb = false) => {
  return isMweb
    ? `${OMNITURE_SORT_MWEB[sortSettings?.currentSort]}`
    : `${OMNITURE_SORT[sortSettings?.currentSort]}${
        OMNITURE_SORT[sortSettings?.currentSort] !== 'Best'
          ? sortSettings.isAscending
            ? '|Low'
            : '|High'
          : ''
      }`;
};

export const getFareType = fareType => {
  return FARE_TYPES[fareType];
};

export const getFareIdentifier = fareType => {
  return FARE_IDENTIFIER[fareType];
};

export const isMetaSource = () => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window?.location?.search);
    return (urlParams.get('utm_source') || '').toLowerCase() === 'meta';
  }
  return false;
};

export const getMedium = () => {
  if (isMetaSource() && typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window?.location?.search);
    return (urlParams.get('utm_medium') || '').toLowerCase();
  }
  return '';
};

export const getIdFromFlightCard = flightItem => {
  try {
    if (flightItem?.id) {
      return flightItem?.id[Object.keys(flightItem?.id)[0]][0];
    }
    return null;
  } catch {
    return null;
  }
};

export const getSearchDetailsForRecentSearch = (data, url) => {
  try {
    const {
      source,
      destination,
      departure,
      arrival,
      journeyType,
      passengers,
      travelType,
      selectedFareIdentifier,
      flightClass
    } = data;
    const searchDetails = {
      searchContext: {
        funnelSource: 'flight',
        lob: 'flights',
        lobCategory: travelType === 'domestic' ? 'DF' : 'IF',
        from: {
          lobCity: source.iata,
          cityName: source.ct,
          lobCountry: source.cnty,
          countryCode: source.cc
        },
        to: {
          lobCity: destination.iata,
          cityName: destination.ct,
          lobCountry: destination.cnty,
          countryCode: destination.cc
        },
        fromDateTime: {
          ts: departure.dateObj.getTime(),
          zone: 'IST',
          timezone: 'IST',
          str: departure.dateForRS
        },
        ...(arrival && {
          toDateTime: {
            ts: arrival.dateObj.getTime(),
            zone: 'IST',
            timezone: 'IST',
            str: arrival.dateForRS
          }
        }),
        pax: [
          {
            details: {
              adult: {
                count: passengers.adult
              },
              child: {
                count: passengers.child
              },
              infant: {
                count: passengers.infant
              }
            },
            count:
              parseInt(passengers.adult, 10) +
              parseInt(passengers.child, 10) +
              parseInt(passengers.infant, 10)
          }
        ],
        journeyType: journeyType.toUpperCase(),
        fareFamily: selectedFareIdentifier,
        cabinClass: flightClass.value,
        meta: {
          deeplink: JSON.stringify({
            mweb: url,
            dweb: url
          }),
          title: `${source.ct} - ${destination.ct}`,
          subtitle: `${departure.dateToDisplay}${
            arrival?.dateToDisplay ? ` - ${arrival.dateToDisplay}` : ''
          }`,
          tg: 102,
          gd: JSON.stringify({
            svid: '',
            src: source?.iata || '',
            scn: source?.ct || '',
            san: source?.n || '',
            sctry: source?.cnty || '',
            dvid: '',
            dst: destination?.iata || '',
            dcn: destination?.ct || '',
            dan: destination?.n || '',
            dctry: destination?.cnty || '',
            i: travelType === 'international' ? '1' : '0', //isInternational
            oi: travelType === 'international' && journeyType === 'ow' ? '1' : '0', //isOnwardInternational
            ri: travelType === 'international' && journeyType === 'rt' ? '1' : '0', //isReturnInternational
            qd: `air-${source.iata}-${destination.iata}-${departure.ddmmyyyy}-${
              arrival ? arrival.ddmmyyyy : 'NA'
            }-${passengers.adult}-${passengers.child}-${passengers.infant}-${flightClass.value}`
          })
        }
      }
    };
    return searchDetails;
  } catch (e) {
    return null;
  }
};

export const getSearchDetailsFromRecentSearch = data => {
  try {
    const {
      funnelSource,
      lob,
      lobCategory,
      from,
      to,
      fromDateTime,
      toDateTime,
      pax,
      journeyType,
      fareFamily,
      cabinClass,
      meta
    } = data;
    const searchDetails = {
      source: {
        iata: from.lobCity,
        ct: from.cityName,
        cnty: from.lobCountry,
        cc: from.countryCode
      },
      destination: {
        iata: to.lobCity,
        ct: to.cityName,
        cnty: to.lobCountry,
        cc: to.countryCode
      },
      departure: new Date(fromDateTime.ts),
      ...(toDateTime && {
        arrival: new Date(toDateTime.ts)
      }),
      journeyType,
      passengers: {
        adult: pax[0]?.details?.adult?.count || 1,
        child: pax[0]?.details?.child?.count || 0,
        infant: pax[0]?.details?.infant?.count || 0
      },
      travelType: lobCategory === 'DF' ? 'domestic' : 'international',
      selectedFareIdentifier: fareFamily,
      flightClass: {
        value: cabinClass,
        name: flightClassList[cabinClass]
      }
    };

    return searchDetails;
  } catch (e) {
    return null;
  }
};

export const filterFlightsFnc = (list, currentFilterState) => {
  try {
    if (!Object.keys(currentFilterState).length) return list;
    const filtered = list.filter(listItem => {
      const listFilters = listItem.filter || [];
      if (!Array.isArray(listFilters) || !listFilters.length) {
        return true;
      }
      return Boolean(
        listFilters.reduce((acc, value, fi) => {
          if (Array.isArray(currentFilterState[fi]) && currentFilterState[fi].length) {
            const currentReducedValue = currentFilterState[fi].reduce((reducedValue, e, i) => {
              if (e === 1) {
                reducedValue += Math.pow(2, i);
              }
              return reducedValue;
            }, 0);
            if (currentReducedValue !== 0) {
              acc &&= value & currentReducedValue;
            }
          }
          return acc;
        }, true)
      );
    });
    return filtered;
  } catch (err) {
    console.log('err :', err);
  }
};

export const getFareKey = (flight, selectedFlight, roundTripFareFamilyIndex) => {
  //for one way flights
  if (selectedFlight?.bundleIndex) {
    return flight.al[selectedFlight.bundleIndex].fk;
  }
  //for round trip flights
  if (roundTripFareFamilyIndex !== null) {
    return flight.al[roundTripFareFamilyIndex].fk;
  }
  return flight.fk;
};

export const openBookingReview = params => {
  window.open(`${window.location.origin}${BOOKING_REVIEW_PAGE_URL}${params}`);
};

export const handleListWithSpillOverBanner = params => {
  if (!params.hasOwnProperty('currentSpilledOverBannersLength')) {
    params['currentSpilledOverBannersLength'] = 0;
  }
  const { insertAtPosition, list = [], maxBannersToSpill = 1, dataToInsert } = params || {};
  const isBannerWillSpillOver = insertAtPosition >= list.length;
  const isMaxSpillReached = params['currentSpilledOverBannersLength'] >= maxBannersToSpill;
  if (isBannerWillSpillOver && isMaxSpillReached) {
    return;
  }
  //only maxBannersToSpill no of banners can spillover
  if (isBannerWillSpillOver) {
    list.splice(list.length, 0, dataToInsert);
    params['currentSpilledOverBannersLength'] += 1;
  } else {
    list.splice(insertAtPosition, 0, dataToInsert);
  }
};

export const getUniqueOmnitureId = () => {
  try {
    const value = getCookie(OMNITURE_ID);
    return decodeURI(value).split('|')[4] || '';
  } catch (error) {
    console.log('LOG: Error in getUniqueOmnitureId =>', error);
    return '';
  }
};

export const shouldFareOptionCardBeDisplayed = (
  fareOptionData,
  flavour,
  flightClass,
  paxCounts,
  travelType,
  journeyType
) => {
  // check if details are available
  const details = fareOptionData.details;
  if (!details || Object.keys(details).length === 0) {
    return false;
  }
  // check flavour
  if (!fareOptionData?.flavour?.includes(flavour)) {
    return false;
  }

  // check Dates
  // const startDate = fareOptionData?.bookingdate?.start;
  // const endDate = fareOptionData?.bookingdate?.end;

  // const departureDate = departure && departure.yyyymmdd;
  // if (
  //   startDate &&
  //   endDate &&
  //   departureDate &&
  //   (departureDate < startDate || departureDate > endDate)
  // ) {
  //   return false;
  // }

  // const arrivalDate = arrival && arrival.yyyymmdd;
  // if (startDate && endDate && arrivalDate && (arrivalDate < startDate || arrivalDate > endDate)) {
  //   return false;
  // }

  // check Class
  if (!fareOptionData?.fare_class?.includes(flightClass?.name?.toLowerCase() || '')) {
    return false;
  }

  // check pax count limit
  const paxProfile = fareOptionData?.paxProfile;
  if (
    paxCounts.adult > paxProfile.adult ||
    paxCounts.child > (paxProfile.child || 0) ||
    paxCounts.infant > (paxProfile.infant || 0)
  ) {
    return false;
  }

  // check travel type
  if (!fareOptionData?.trip_sector?.includes(travelType?.toLowerCase() || '')) {
    return false;
  }

  // check journey type
  if (!fareOptionData?.trip_type?.includes(journeyType?.toLowerCase() || '')) {
    return false;
  }

  return true;
};
